import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva } from 'class-variance-authority';
import { type ReactNode, forwardRef } from 'react';

import { Spinner } from '@/components/loaders';
import { cn } from '@/lib/utils';

/* eslint-disable lingui/no-unlocalized-strings */

// ─── Shared class groups ─────────────────────────────────────────────────────

const pseudoElementBase = [
  'transition-all duration-200 ease-in-out',
  'before:content-[""] before:absolute before:inset-0',
  'before:border-0 before:border-transparent before:bg-clip-padding',
  'before:transition-all before:duration-200 before:ease-in-out',
  'after:content-[""] after:absolute after:inset-0',
  'after:border after:border-transparent',
  'after:transition-all after:duration-200 after:ease-in-out',
  'focus-visible:after:border-dashed focus:after:border-dashed',
  'hover:after:border-solid',
  'active:after:border-solid',
];

const ghostBase = [
  'border border-transparent',
  'transition-colors duration-150',
  'focus-visible:border-1',
];

// ─── Variant key type ────────────────────────────────────────────────────────

type VariantKey =
  | 'secondary'
  | 'secondary-danger'
  | 'secondary-inverse'
  | 'primary'
  | 'primary-danger'
  | 'on-primary'
  | 'ghost'
  | 'ghost-danger'
  | 'ghost-inverse';

// ─── CVA definition ──────────────────────────────────────────────────────────

const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center',
    'font-sans font-semibold whitespace-nowrap',
    'cursor-pointer disabled:cursor-not-allowed aria-disabled:cursor-not-allowed',
    'shrink-0 outline-none',
    'no-underline hover:no-underline',
    'visited:text-current',
  ],
  {
    variants: {
      variant: {
        // ── Primary ──
        primary: [
          ...pseudoElementBase,
          'text-gray-900',
          'before:bg-primary',
          'after:border-primary',
          'focus-visible:before:bg-green-400 focus-visible:after:border-green-400',
          'focus:before:bg-green-400 focus:after:border-green-400',
          'hover:before:bg-green-400 hover:after:border-green-400',
          'hover:focus:before:bg-green-400 hover:focus:after:border-green-400',
          'active:before:bg-green-500 active:focus:before:bg-green-500 active:after:border-green-500 active:focus:after:border-green-500',
          'disabled:text-gray-500 disabled:before:bg-gray-200 disabled:after:border-gray-200',
          'aria-disabled:text-gray-500 aria-disabled:before:bg-gray-200 aria-disabled:after:border-gray-200',
        ],
        'primary-danger': [
          ...pseudoElementBase,
          'text-red-400',
          'before:bg-red-100',
          'after:border-red-100',
          'focus-visible:text-white focus-visible:before:bg-red-400 focus-visible:after:border-red-400',
          'focus:text-white focus:before:bg-red-400 focus:after:border-red-400',
          'hover:text-white hover:before:bg-red-400 hover:after:border-red-400',
          'hover:focus:text-white hover:focus:before:bg-red-400 hover:focus:after:border-red-400',
          'active:text-white active:before:bg-red-500 active:focus:before:bg-red-500 active:after:border-red-500 active:focus:after:border-red-500',
          'disabled:text-red-100 disabled:before:bg-red-50 disabled:after:border-red-50',
          'aria-disabled:text-red-100 aria-disabled:before:bg-red-50 aria-disabled:after:border-red-50',
        ],
        'on-primary': [
          ...pseudoElementBase,
          'text-primary',
          'before:bg-gray-900',
          'after:border-gray-900',
          'focus-visible:text-green-400 focus-visible:before:bg-gray-900 focus-visible:after:border-gray-900',
          'focus:text-green-400 focus:before:bg-gray-900 focus:after:border-gray-900',
          'hover:text-white hover:before:bg-gray-900 hover:after:border-gray-900',
          'hover:focus:text-white hover:focus:before:bg-gray-900 hover:focus:after:border-gray-900',
          'active:focus:text-green-500 active:before:bg-gray-900 active:focus:before:bg-gray-900 active:after:border-gray-900 active:focus:after:border-gray-900',
          'disabled:text-gray-500 disabled:before:bg-gray-200 disabled:after:border-gray-200',
          'aria-disabled:text-gray-500 aria-disabled:before:bg-gray-200 aria-disabled:after:border-gray-200',
        ],
        // ── Secondary (default) ──
        secondary: [
          ...pseudoElementBase,
          'text-gray-900',
          'before:bg-white',
          'after:border-gray-900',
          'focus-visible:text-white focus-visible:before:bg-gray-900 focus-visible:after:border-gray-900',
          'focus:text-white focus:before:bg-gray-900 focus:after:border-gray-900',
          'hover:text-white hover:before:bg-gray-800 hover:after:border-gray-800',
          'hover:focus:text-white hover:focus:before:bg-gray-800 hover:focus:after:border-gray-800',
          'active:text-white active:before:bg-gray-900 active:focus:before:bg-gray-900 active:after:border-gray-900 active:focus:after:border-gray-900',
          'disabled:text-gray-400 disabled:before:bg-white disabled:after:border-gray-400',
          'aria-disabled:text-gray-400 aria-disabled:before:bg-white aria-disabled:after:border-gray-400',
        ],
        'secondary-danger': [
          ...pseudoElementBase,
          'text-red-400',
          'before:bg-white',
          'after:border-red-400',
          'focus-visible:text-white focus-visible:before:bg-red-400 focus-visible:after:border-red-400',
          'focus:text-white focus:before:bg-red-400 focus:after:border-red-400',
          'hover:text-white hover:before:bg-red-400 hover:after:border-red-400',
          'hover:focus:text-white hover:focus:before:bg-red-400 hover:focus:after:border-red-400',
          'active:text-white active:before:bg-red-500 active:focus:before:bg-red-500 active:after:border-red-500 active:focus:after:border-red-500',
          'disabled:text-red-100 disabled:before:bg-white disabled:after:border-red-50',
          'aria-disabled:text-red-100 aria-disabled:before:bg-white aria-disabled:after:border-red-50',
        ],
        'secondary-inverse': [
          ...pseudoElementBase,
          'text-white',
          'before:bg-transparent',
          'after:border-white',
          'focus-visible:text-gray-900 focus-visible:before:bg-white focus-visible:after:border-white',
          'focus:text-gray-900 focus:before:bg-white focus:after:border-white',
          'hover:text-gray-900 hover:before:bg-white hover:after:border-white',
          'hover:focus:text-gray-900 hover:focus:before:bg-white hover:focus:after:border-white',
          'active:text-gray-900 active:before:bg-gray-100 active:focus:before:bg-gray-100 active:after:border-gray-100 active:focus:after:border-gray-100',
          'disabled:text-gray-400 disabled:before:bg-transparent disabled:after:border-gray-400',
          'aria-disabled:text-gray-400 aria-disabled:before:bg-transparent aria-disabled:after:border-gray-400',
        ],
        // ── Ghost ──
        ghost: [
          ...ghostBase,
          'text-gray-900 bg-transparent',
          'focus-visible:bg-black/10',
          'hover:bg-black/10',
          'active:bg-black/20',
          'disabled:text-gray-400 disabled:bg-transparent disabled:border-transparent',
          'aria-disabled:text-gray-400 aria-disabled:bg-transparent aria-disabled:border-transparent',
        ],
        'ghost-danger': [
          ...ghostBase,
          'text-red-400 bg-transparent',
          'focus-visible:bg-red-300/20',
          'hover:bg-red-300/20',
          'active:bg-red-300/30',
          'disabled:text-red-100 disabled:bg-transparent disabled:border-transparent',
          'aria-disabled:text-red-100 aria-disabled:bg-transparent aria-disabled:border-transparent',
        ],
        'ghost-inverse': [
          ...ghostBase,
          'text-white bg-transparent',
          'focus-visible:bg-white/20',
          'hover:bg-white/20',
          'active:bg-white/30',
          'disabled:text-gray-400 disabled:bg-transparent disabled:border-transparent',
          'aria-disabled:text-gray-400 aria-disabled:bg-transparent aria-disabled:border-transparent',
        ],
      } satisfies Record<VariantKey, string[]>,
      size: {
        small: ['h-6 gap-1 text-xs leading-none', '[&_svg]:size-3'],
        medium: ['h-8 gap-1.5 text-xs leading-none', '[&_svg]:size-4'],
        large: ['h-10 gap-1.5 text-base leading-none', '[&_svg]:size-5'],
        'icon-small': ['size-6 text-xs leading-none', '[&_svg]:size-4'],
        icon: ['size-8 text-xs leading-none', '[&_svg]:size-5'],
        'icon-large': ['size-10 text-base leading-none', '[&_svg]:size-6'],
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'medium',
    },
  },
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

const resolveVariantKey = (
  variant: 'primary' | 'secondary' | 'ghost' | 'on-primary',
  danger?: boolean,
  inverse?: boolean,
): VariantKey => {
  if (variant === 'on-primary') return variant;
  // inverse is not valid on primary — fall back to plain primary
  if (danger) return `${variant}-danger` as VariantKey;
  if (inverse && variant !== 'primary') return `${variant}-inverse` as VariantKey;
  return variant;
};

const FOCUS_BORDER_SM =
  'focus-visible:before:border-3 focus:before:border-3 hover:before:!border-0 active:before:!border-0';
const FOCUS_BORDER_MD =
  'focus-visible:before:border-4 focus:before:border-4 hover:before:!border-0 active:before:!border-0';

type BaseSize = 'small' | 'medium' | 'large';
type IconSize = 'icon-small' | 'icon' | 'icon-large';
export type ButtonSize = BaseSize | IconSize;
const PADDING_FILLED_ROUND_BY_BUTTON_SIZE: Record<ButtonSize, string> = {
  small: 'px-3',
  medium: 'px-4',
  large: 'px-5',
  'icon-small': 'p-0',
  icon: 'p-0',
  'icon-large': 'p-0',
};
const PADDING_FILLED_SQUARE_BY_BUTTON_SIZE: Record<ButtonSize, string> = {
  small: 'px-1.5',
  medium: 'px-2.5',
  large: 'px-3.5',
  'icon-small': 'p-0',
  icon: 'p-0',
  'icon-large': 'p-0',
};
const PADDING_GHOST_BY_BUTTON_SIZE: Record<ButtonSize, string> = {
  small: 'px-1.5',
  medium: 'px-2.5',
  large: 'px-3.5',
  'icon-small': 'p-0',
  icon: 'p-0',
  'icon-large': 'p-0',
};
const LOADING_SIZE_BY_BUTTON_SIZE: Record<ButtonSize, 'xs' | 'sm' | 'md'> = {
  small: 'xs',
  medium: 'sm',
  large: 'md',
  'icon-small': 'xs',
  icon: 'sm',
  'icon-large': 'md',
};
const FOCUS_BORDER_BY_BUTTON_SIZE: Record<ButtonSize, string> = {
  small: FOCUS_BORDER_SM,
  medium: FOCUS_BORDER_MD,
  large: FOCUS_BORDER_MD,
  'icon-small': FOCUS_BORDER_SM,
  icon: FOCUS_BORDER_MD,
  'icon-large': FOCUS_BORDER_MD,
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type ButtonProps = ButtonPrimitive.Props & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'on-primary';
  size?: ButtonSize;
  shape?: 'round' | 'square';
  danger?: boolean;
  inverse?: boolean;
  isLoading?: boolean;
  block?: boolean;
};

type ButtonClassNameArgs = Pick<
  ButtonProps,
  'variant' | 'size' | 'shape' | 'danger' | 'inverse' | 'block' | 'className'
>;

export const getButtonClassName = ({
  variant = 'secondary',
  size = 'medium',
  shape = 'round',
  danger,
  inverse,
  block = false,
  className,
}: ButtonClassNameArgs) => {
  const isGhost = variant === 'ghost';
  const variantKey = resolveVariantKey(variant, danger, inverse);

  const paddingClass = isGhost
    ? PADDING_GHOST_BY_BUTTON_SIZE[size]
    : shape === 'square'
      ? PADDING_FILLED_SQUARE_BY_BUTTON_SIZE[size]
      : PADDING_FILLED_ROUND_BY_BUTTON_SIZE[size];

  const isSmallSize = size === 'small' || size === 'icon-small';
  const squareRadius = isSmallSize ? 'rounded-sm' : 'rounded-md';
  const squarePseudoRadius = isSmallSize
    ? 'before:rounded-sm after:rounded-sm'
    : 'before:rounded-md after:rounded-md';

  const shapeClass = block
    ? isGhost
      ? squareRadius
      : isSmallSize
        ? 'before:rounded-sm! after:rounded-sm!'
        : 'before:rounded-md! after:rounded-md!'
    : isGhost
      ? shape === 'round'
        ? 'rounded-full'
        : squareRadius
      : shape === 'round'
        ? 'before:rounded-full after:rounded-full'
        : squarePseudoRadius;

  const focusBorderClass = !isGhost ? FOCUS_BORDER_BY_BUTTON_SIZE[size] : undefined;

  const blockClass = block ? 'w-full' : undefined;

  return {
    className: cn(
      buttonVariants({ variant: variantKey, size }),
      paddingClass,
      shapeClass,
      focusBorderClass,
      blockClass,
      className,
    ),
    loadingSize: LOADING_SIZE_BY_BUTTON_SIZE[size],
  };
};

type ButtonContentProps = {
  children?: ReactNode;
  isLoading?: boolean;
  loadingSize: 'xs' | 'sm' | 'md';
};

export const renderButtonContent = ({
  children,
  isLoading = false,
  loadingSize,
}: ButtonContentProps) => (
  <>
    {isLoading && (
      <span className="absolute top-1/2 left-1/2 z-20 -translate-1/2">
        <Spinner variant="spoke" size={loadingSize} className="text-inherit" />
      </span>
    )}
    {children && (
      <span
        className={cn('relative inline-flex items-center gap-[inherit]', isLoading && 'invisible')}
      >
        {children}
      </span>
    )}
  </>
);

// ─── Component ───────────────────────────────────────────────────────────────

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      children,
      variant = 'secondary',
      size = 'medium',
      shape = 'round',
      type = 'button',
      isLoading = false,
      disabled = false,
      block = false,
      danger,
      inverse,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const { className: computedClassName, loadingSize } = getButtonClassName({
      variant,
      size,
      shape,
      danger,
      inverse,
      block,
      className,
    });

    const handleClick: ButtonProps['onClick'] = (event) => {
      if (disabled || isLoading) {
        event.stopPropagation();
        event.preventDefault();
        return;
      }
      onClick?.(event);
    };

    const content = renderButtonContent({ children, isLoading, loadingSize });

    const button = (
      <ButtonPrimitive
        data-slot="button"
        ref={ref}
        type={type}
        className={computedClassName}
        disabled={disabled}
        aria-busy={isLoading || undefined}
        onClick={handleClick}
        {...rest}
      >
        {content}
      </ButtonPrimitive>
    );
    return button;
  },
);

Button.displayName = 'Button';

export default Button;
